import { useState, useRef } from "react";
import { Popper, Paper, ClickAwayListener, Box, Typography, Stack, Avatar, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { RouteTo, RouteToSettings } from "@routing/RouteMap";
import { useQuery } from "@apollo/client";
import { FETCH_USER_PROFILE } from "@company/graphql/queries/fetchUserProfile";
import useAuthActions from "@auth/hooks/useAuthActions";
import { alpha } from "@mui/material/styles";
import { useIsAdmin } from "@auth/hooks/useIsAdmin";
import { useIsAuthenticated } from "@auth/hooks/useIsAuthenticated";
import { useAdminAuthDetection } from "@auth/hooks/useAdminAuthDetection";

const UserMenu = () => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLDivElement>(null);
    const isAuthenticated = useIsAuthenticated();
    const { data: profile } = useQuery(FETCH_USER_PROFILE, { skip: !isAuthenticated });
    const navigate = useNavigate();
    const { logout } = useAuthActions();
    const { isAdmin } = useIsAdmin();
    const { isAdminAuthenticated } = useAdminAuthDetection();

    const handleLogout = async () => {
        logout();
    };

    const handleSwitchToAdmin = () => {
        // Open admin interface in new tab - admin should already be authenticated
        const adminUrl = `${window.location.origin}/admin/dashboard`;
        window.open(adminUrl, '_blank', 'noopener,noreferrer');
        setOpen(false);
    };

    const MenuItem = ({ label, action }: { label: string; action: { redirect?: string; action?: () => void } }) => {
        return (
            <Box
                sx={{
                    p: 1,
                    borderRadius: 1,
                    cursor: "pointer",
                    "&:hover": {
                        bgcolor: "semantic.surface.secondary",
                    },
                }}
                onClick={() => {
                    setOpen(false);
                    if (action.action) {
                        action.action();
                    }
                    if (action.redirect) {
                        navigate(action.redirect);
                    }
                }}
            >
                <Typography variant="body2">{label}</Typography>
            </Box>
        );
    };

    return (
        <div>
            {/* Rectangular hover area that contains the avatar */}
            <Box
                ref={anchorRef}
                onClick={() => setOpen(!open)}
                onMouseEnter={() => setOpen(true)}
                sx={{
                    px: 1,
                    pb: 1, // this messes with the layout... would love to set it to py: 1...
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    borderRadius: 1,
                }}
            >
                {isAuthenticated ? (
                    <Avatar sx={{ backgroundColor: "semantic.surface.primary" }}>{profile?.userProfile?.name.charAt(0)}</Avatar>
                ) : (
                    <Avatar sx={{ backgroundColor: "semantic.surface.primary" }}>
                        <Typography variant="button">G</Typography>
                    </Avatar>
                )}
            </Box>

            <Popper
                open={open}
                anchorEl={anchorRef.current}
                placement="bottom-end"
                modifiers={[
                    {
                        name: "offset",
                        options: {
                            offset: [0, 0], // Remove gap
                        },
                    },
                ]}
                sx={{ zIndex: 1200 }} // Set above AppBar's z-index
            >
                <ClickAwayListener onClickAway={() => setOpen(false)}>
                    <Paper
                        onMouseLeave={() => setOpen(false)}
                        sx={{
                            p: 1.5,
                            minWidth: "200px",
                            bgcolor: "semantic.surface.primary",
                            border: `1px solid ${alpha(theme.palette.semantic.button.primary, 0.5)}`,
                            borderRadius: 1,
                        }}
                        elevation={0}
                    >
                        <Stack spacing={2}>
                            {/* Profile Section */}
                            {/* <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                                <UserAvatar size={40} />
                                <Box>
                                    <Typography variant="subtitle1">{profile?.userProfile.name || "User"}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {profile?.email}
                                    </Typography>
                                </Box>
                            </Box> 

                            <Divider /> */}

                            {/* Menu Items */}
                            {isAuthenticated ? (
                                <Stack spacing={1}>
                                    <MenuItem label="Mein Profil" action={{ redirect: RouteToSettings.PROFILE_GENERAL }} />
                                    <MenuItem
                                        label="Meine Firma"
                                        action={{
                                            redirect: isAdmin ? RouteToSettings.COMPANY_PROFILE : RouteToSettings.COMPANY_PROFILE_READONLY,
                                        }}
                                    />
                                    {isAdmin && (
                                        <MenuItem label="ESGbot Shop" action={{ redirect: RouteToSettings.SHOP_SAAS }} />
                                    )}
                                    {isAdminAuthenticated && (
                                        <MenuItem label="🔧 Zum Admin-Bereich" action={{ action: handleSwitchToAdmin }} />
                                    )}
                                    <MenuItem label="Logout" action={{ action: handleLogout }} />
                                </Stack>
                            ) : (
                                <Stack spacing={1}>
                                    <MenuItem label="Login" action={{ redirect: RouteTo.LOGIN }} />
                                    <MenuItem
                                        label="Jetzt Registrieren"
                                        action={{ redirect: RouteTo.ONBOARDING }}
                                        // highlight styling
                                    />
                                </Stack>
                            )}
                        </Stack>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </div>
    );
};

export default UserMenu;
